import { IntlShape } from 'react-intl';

// Because 0 is a valid, non-empty currency value, but in Javascript 0 is falsy
export const currencyValueExists = (value: number | undefined | null) =>
  !!(value || (value !== undefined && value !== null && value == 0));

// Because 0 is a valid, non-empty currency value, but in Javascript 0 is falsy
export const currencyValueOrDefault = (
  value: number | undefined | null,
  defaultValue: number | undefined | null,
  defaultValue2?: number | undefined | null,
  defaultValue3?: number | undefined | null
) =>
  currencyValueExists(value)
    ? value
    : currencyValueExists(defaultValue)
      ? defaultValue
      : currencyValueExists(defaultValue2)
        ? defaultValue2
        : currencyValueExists(defaultValue3)
          ? defaultValue3
          : null;

// Given: 64.269999, '$'
// Returns: '$64.27'
export const currencyMainUnitsFormatter = (
  value: number | string,
  currencySymbol: string
) => {
  return `${currencySymbol}${Number(value).toFixed(2)}`;
};

// Given: a two-letter countryCode e.g. 'GB'
// Returns: a two-letter language code e.g. 'en'
//
// We pick the single most commonly-used language for the country - yes, a country can have multiple languages,
// but we don't often run into issues around that, and this simplifying assumption makes our lives a lot easier -
// if this becomes an issue, revisit
const countryCodeToLanguage = (countryCode: string) =>
  // 'und' = undetermined language in ISO-speak
  new Intl.Locale('und', { region: countryCode })?.maximize()?.language;

// Given: a two-letter countryCode e.g. 'GB'
// Returns: a locale e.g. 'en-GB'
const countryCodeToLocale = (countryCode: string) => {
  // This test if countryCode contains a value and the fallback/default return value are only for handling re-renders
  if (countryCode) {
    const language = countryCodeToLanguage(countryCode);
    return `${language}-${countryCode}`;
  } else {
    return 'en-GB';
  }
};

// Returns: an integer representing number of decimal places used by currency's MAJOR units e.g. 2
const localeAndCurrencyCodeToNumDecimalPlaces = (currencyCode: string) => {
  if (!currencyCode) {
    return 0;
  }

  // We always just pass in 'en-US' for locale here b/c passing in other locales has no effect on the
  // number of decimal places used by the currency
  const numberFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });
  return (
    numberFormat.formatToParts(1).find((part) => part.type === 'fraction')
      ?.value.length ?? 0
  );
};

// Given:
// value = a NUMBER (integer) containing currency amount in smallest units for the currency (e.g. cents) e.g. 3000
// currencyCode = three-letter ISO currency code
//
// Returns: the value as a STRING expressed in given number of decimal places e.g. '30.00'
const convertValueToMajorUnitsString = (
  value: number,
  currencyCode: string
) => {
  const numDecimalPlaces =
    localeAndCurrencyCodeToNumDecimalPlaces(currencyCode);

  if (numDecimalPlaces == 0) {
    return String(value);
  }

  const denominator = 10 ** numDecimalPlaces;
  return (value / denominator).toFixed(numDecimalPlaces);
};

// Given:
// value = a STRING containing currency amount with EITHER just major units as an integer e.g. '30'
//         OR major units + minor units as a decimal e.g. '30.00'
// currencyCode = three-letter ISO currency code
//
// Returns: the value as a NUMBER (integer) expressed in smallest units for the currency e.g. 3000
const convertValueToMinorUnitsNumber = (
  value: string,
  currencyCode: string
) => {
  const numDecimalPlaces =
    localeAndCurrencyCodeToNumDecimalPlaces(currencyCode);

  if (numDecimalPlaces == 0) {
    return Math.floor(Number(value));
  }

  const multiplier = 10 ** numDecimalPlaces;
  return Math.floor(Number(value) * multiplier);
};

const ensureRealSpaceCharacters = (value: string) => value.replace(/\s/g, ' ');
const stripMinorUnits = (value: string) => value.replace(/\.\d+$/, '');
const capitalizeFirstChar = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

// Given:
// value = an integer containing currency amount in smallest units for the currency (e.g. cents) e.g. 3000
// countryCode = two-letter ISO country code
// currencyCode = three-letter ISO currency code
// onlyMajorUnits = true/false - if true, returns value rounded down to nearest major unit and omitting all minor units e.g. '$30'
//
// Returns: a string containing fully-formatted currency value for display e.g. '$30.00' or '$30'
//
// For most uses currently (March 2025), we don't want to display in another locale, we WANT the en-US style display,
// for example contrast these two versions, where currencyCode=eur:
// - Version 1 - if countryCode=ES and hence locale=es-ES:
//     '189,75 €'
// - Version 2 - if countryCode=US and hence locale=en-US:
//     '€189.75'
// For consistent visual style when displaying different currencies side-by-side (e.g. when viewing a list of events across
// different cities/countries in event planner), we prefer version 2 above
export const currencyFormatter = ({
  value,
  countryCode = 'US',
  currencyCode,
  onlyMajorUnits = false,
}: {
  value: number;
  countryCode?: string;
  currencyCode?: string;
  onlyMajorUnits?: boolean;
}) => {
  const countryCodeToUse = countryCode?.toUpperCase() || '';
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  // e.g. 'en-GB'
  const locale = countryCodeToLocale(countryCodeToUse);

  const valueInMajorUnits = Number(
    convertValueToMajorUnitsString(value, currencyCodeToUse)
  );

  const additionalOptions = onlyMajorUnits
    ? {
        roundingMode: 'floor',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }
    : {};

  const formattedValue = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCodeToUse,
    ...additionalOptions,
  }).format(valueInMajorUnits);

  // We get funky space-like characters acting as spaces back from Intl.NumberFormat - to avoid
  // display complications (including with tests), replace those with simple space characters
  return ensureRealSpaceCharacters(formattedValue);
};

// Given:
// value = a NUMBER (integer) containing currency amount in smallest units for the currency (e.g. cents) e.g. 3000
// currencyCode = three-letter ISO currency code, e.g. 'usd'
// onlyMajorUnits = true/false - if true, returns value rounded down to nearest major unit and omitting all minor units e.g. '30'
//
// Returns: a STRING currency value for pre-populating into form fields e.g. '30.00' or '30'
//
// IMPORTANT: We always return a STRING so that our form fields behave correctly, including all validation
export const currencyFormFieldFormatter = ({
  value,
  currencyCode,
  onlyMajorUnits = false,
}: {
  value: number;
  currencyCode?: string;
  onlyMajorUnits?: boolean;
}) => {
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  let valueInMajorUnits = convertValueToMajorUnitsString(
    value,
    currencyCodeToUse
  );

  if (onlyMajorUnits) {
    valueInMajorUnits = stripMinorUnits(valueInMajorUnits);
  }

  return valueInMajorUnits;
};

// Given:
// value = a STRING containing currency amount with EITHER just major units as an integer e.g. '30'
//         OR major units + minor units as a decimal e.g. '30.00'
// currencyCode = three-letter ISO currency code, e.g. 'usd'
//
// Returns: a NUMBER (integer) containing currency amount in smallest units for the currency e.g. 3000
//
// IMPORTANT: We always return a NUMBER because backend and frontend always pass integers back and forth
// to represent currency values
export const currencySubmitVariableFormatter = ({
  value,
  currencyCode,
}: {
  value: string;
  currencyCode?: string;
}) => {
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  const valueInMinorUnits = convertValueToMinorUnitsNumber(
    value,
    currencyCodeToUse
  );

  return valueInMinorUnits;
};

// Given:
// value = a STRING containing currency amount
// currencyCode = three-letter ISO currency code, e.g. 'usd'
//
// Returns: true if value is a valid currency value containing minor units for the given currency, false if not -
// a valid value for e.g. US would be '34.50' - value is always invalid if it contains a currency symbol or commas
//
// Used for validation in Yup form schemas - see also invalidCurrencyValueMessage
export const isValidCurrencyValue = ({
  value,
  currencyCode,
}: {
  value: string;
  currencyCode?: string;
}) => {
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  const numDecimalPlaces =
    localeAndCurrencyCodeToNumDecimalPlaces(currencyCodeToUse);

  if (numDecimalPlaces == 0) {
    return /^\d+$/.test(value);
  } else if (numDecimalPlaces == 1) {
    return /^\d+\.\d$/.test(value);
  } else if (numDecimalPlaces == 2) {
    return /^\d+\.\d\d$/.test(value);
  } else if (numDecimalPlaces == 3) {
    return /^\d+\.\d\d\d$/.test(value);
  } else {
    return false;
  }
};

// Given:
// fieldDisplayName = display name of field (string) e.g. 'artist compensation'
// intl = React intl object
// currencyCode = three-letter ISO currency code, e.g. 'usd'
//
// Returns: Validation message (string) to display to user IF isValidCurrencyValue returns false, for the given currency
//
// Used for validation in Yup form schemas - see also isValidCurrencyValue
export const invalidCurrencyValueMessage = ({
  fieldDisplayName,
  intl,
  currencyCode,
}: {
  fieldDisplayName: string;
  intl: IntlShape;
  currencyCode?: string;
}) => {
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  const numDecimalPlaces =
    localeAndCurrencyCodeToNumDecimalPlaces(currencyCodeToUse);

  const validationMessageTextKeys = [
    'admin.shared.currencyAmount0DecimalsError',
    'admin.shared.currencyAmount1DecimalsError',
    'admin.shared.currencyAmount2DecimalsError',
    'admin.shared.currencyAmount3DecimalsError',
  ];

  let textKey: string = '';

  if (validationMessageTextKeys[numDecimalPlaces]) {
    textKey = validationMessageTextKeys[numDecimalPlaces];
  } else {
    textKey = validationMessageTextKeys[0];
  }

  return intl.formatMessage(
    {
      id: textKey,
    },
    {
      fieldName: capitalizeFirstChar(fieldDisplayName),
    }
  );
};

// Given:
// currencyCode = three-letter ISO currency code
//
// Returns: the currency symbol for that currency e.g. '£'
export const getCurrencySymbol = (currencyCode?: string) => {
  const currencyCodeToUse = currencyCode?.toUpperCase() || '';

  // We hard-code 'US' here b/c it doesn't seem to matter which country code you specify when you're retrieving the currency symbol
  return (0)
    .toLocaleString('US', {
      style: 'currency',
      currency: currencyCodeToUse,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
};
